/*
 * @Description: 地图点位 — 批量录入（对齐设计图 图3/图4）
 *   ① 下载模板（名称/地址/经纬度）+ 上传 .xls/.xlsx；
 *   ② 解析逐行校验：有经纬度直接用，只有地址则前端 geocode(F2) 补坐标，
 *      失败归类（缺名称 / 经纬度格式错误 / 地址无法识别）；
 *   ③ 识别结果两表（成功 / 失败）→ 确定导入 E4。
 *   产品确认：不要类型/围栏，图标用默认预设；地址前端解析。
 */
import React, { useCallback, useState } from 'react'
import { Button, Table, Upload, message } from 'antd'
import {
  DownloadOutlined,
  FileExcelOutlined,
  InboxOutlined,
  LoadingOutlined
} from '@ant-design/icons'
import { ColumnProps } from 'antd/lib/table'
import Excel from 'exceljs'
import FileSaver from 'file-saver'
import { geocode, importPoi } from '../../../../actions/locationCockpit'
import { useAppDispatch } from '../../../../app/hooks'
import {
  isCockpitMockEnabled,
  POI_ICON_TYPES,
  POI_MAX_COUNT
} from '../../constants'
import { MOCK_GEOCODE } from '../../mock/geo.mock'
import { CockpitEnvelope } from '../../types/cockpit.types'
import {
  GeocodeResult,
  PoiImportItem,
  PoiImportResult
} from '../../types/poi.types'

const { Dragger } = Upload

/** 导入点位默认图标（无图标列，统一给「客户」预设） */
const DEFAULT_ICON = POI_ICON_TYPES[0].iconUrl

interface PoiImporterProps {
  groupId: string
  /** 取消（返回列表） */
  onCancel: () => void
  /** 导入完成（返回列表并刷新） */
  onDone: () => void
}

/** 解析成功的点位行 */
interface OkRow {
  seq: number
  name: string
  address: string
  lng: number
  lat: number
}
/** 解析失败的点位行 */
interface BadRow {
  seq: number
  name: string
  reason: string
  latText: string
  lngText: string
}

/** 取 exceljs 单元格的纯文本 */
function cellText(cell: Excel.Cell): string {
  const v = cell.value
  if (v == null) return ''
  if (typeof v === 'object') {
    const obj = v as {
      text?: unknown
      result?: unknown
      richText?: Array<{ text: string }>
    }
    if (Array.isArray(obj.richText))
      return obj.richText.map((t) => t.text).join('')
    if (obj.text != null) return String(obj.text)
    if (obj.result != null) return String(obj.result)
    return ''
  }
  return String(v)
}

/** 下载导入模板（名称/地址/纬度/经度，含一行示例） */
function downloadTemplate() {
  const wb = new Excel.Workbook()
  const ws = wb.addWorksheet('点位')
  ws.columns = [
    { header: '名称', key: 'name', width: 24 },
    { header: '地址', key: 'address', width: 40 },
    { header: '纬度', key: 'lat', width: 16 },
    { header: '经度', key: 'lng', width: 16 }
  ]
  ws.getRow(1).font = { bold: true }
  ws.addRow({
    name: '天府总部大厦',
    address: '天府大道北段128号',
    lat: 30.5728,
    lng: 104.0668
  })
  wb.xlsx.writeBuffer().then((buf) => {
    const blob = new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
    FileSaver.saveAs(blob, '点位导入模板.xlsx')
  })
}

const PoiImporter: React.FC<PoiImporterProps> = ({
  groupId,
  onCancel,
  onDone
}) => {
  const dispatch = useAppDispatch()
  const isMock = isCockpitMockEnabled()
  const [step, setStep] = useState<'upload' | 'result'>('upload')
  const [parsing, setParsing] = useState(false)
  const [importing, setImporting] = useState(false)
  const [okRows, setOkRows] = useState<OkRow[]>([])
  const [badRows, setBadRows] = useState<BadRow[]>([])

  /** 地址→坐标（mock 直接给固定坐标） */
  const geocodeAddress = useCallback(
    (address: string): Promise<GeocodeResult | null> => {
      if (isMock) return Promise.resolve(MOCK_GEOCODE)
      return dispatch(geocode({ address }))
        .then((res: CockpitEnvelope<GeocodeResult>) =>
          res && res.code === 200 && res.data ? res.data : null
        )
        .catch(() => null)
    },
    [dispatch, isMock]
  )

  /** 解析 + 逐行校验上传文件 */
  const handleFile = useCallback(
    (file: File) => {
      setParsing(true)
      file
        .arrayBuffer()
        .then((buf) => new Excel.Workbook().xlsx.load(buf))
        .then(async (wb) => {
          const ws = wb.worksheets[0]
          // 收集数据行（跳过表头第 1 行）
          const raw: Array<{
            seq: number
            name: string
            address: string
            latText: string
            lngText: string
          }> = []
          ws.eachRow((row, rowNumber) => {
            if (rowNumber === 1) return
            if (raw.length >= POI_MAX_COUNT) return
            raw.push({
              seq: rowNumber,
              name: cellText(row.getCell(1)).trim(),
              address: cellText(row.getCell(2)).trim(),
              latText: cellText(row.getCell(3)).trim(),
              lngText: cellText(row.getCell(4)).trim()
            })
          })

          const ok: OkRow[] = []
          const bad: BadRow[] = []
          for (const r of raw) {
            if (!r.name) {
              bad.push({
                seq: r.seq,
                name: '(空)',
                reason: '缺少点位名称',
                latText: r.latText,
                lngText: r.lngText
              })
              continue
            }
            const hasLngLat = r.latText !== '' && r.lngText !== ''
            if (hasLngLat) {
              const lat = Number(r.latText)
              const lng = Number(r.lngText)
              if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                bad.push({
                  seq: r.seq,
                  name: r.name,
                  reason: '经纬度格式错误',
                  latText: r.latText,
                  lngText: r.lngText
                })
              } else {
                ok.push({
                  seq: r.seq,
                  name: r.name,
                  address: r.address,
                  lng,
                  lat
                })
              }
              continue
            }
            // 无经纬度 → 按地址前端解析坐标
            if (!r.address) {
              bad.push({
                seq: r.seq,
                name: r.name,
                reason: '地址无法识别',
                latText: r.latText,
                lngText: r.lngText
              })
              continue
            }
            const geo = await geocodeAddress(r.address)
            if (geo) {
              ok.push({
                seq: r.seq,
                name: r.name,
                address: r.address,
                lng: geo.lng,
                lat: geo.lat
              })
            } else {
              bad.push({
                seq: r.seq,
                name: r.name,
                reason: '地址无法识别',
                latText: r.latText,
                lngText: r.lngText
              })
            }
          }

          if (ok.length === 0 && bad.length === 0) {
            message.warning('未识别到点位数据，请按模板填写')
            setParsing(false)
            return
          }
          setOkRows(ok)
          setBadRows(bad)
          setParsing(false)
          setStep('result')
        })
        .catch(() => {
          setParsing(false)
          message.error('文件解析失败，请用模板重新填写')
        })
      return false
    },
    [geocodeAddress]
  )

  /** 确定导入（仅成功行） */
  const handleConfirm = useCallback(() => {
    if (okRows.length === 0) return
    const items: PoiImportItem[] = okRows.map((r) => ({
      name: r.name,
      address: r.address,
      lng: r.lng,
      lat: r.lat,
      iconUrl: DEFAULT_ICON
    }))
    if (isMock) {
      message.success(`已导入 ${items.length} 个点位（mock 不持久）`)
      onDone()
      return
    }
    setImporting(true)
    dispatch(
      importPoi({ groupId, skipDupCheck: false, forceAdd: false, items })
    )
      .then((res: CockpitEnvelope<PoiImportResult>) => {
        setImporting(false)
        if (res && res.code === 200 && res.data) {
          const { successCount, failCount } = res.data
          message.success(
            `导入完成：成功 ${successCount} 个${
              failCount ? `，失败 ${failCount} 个` : ''
            }`
          )
          onDone()
        } else {
          message.error((res && res.toast_msg) || '导入失败')
        }
      })
      .catch(() => {
        setImporting(false)
        message.error('导入失败')
      })
  }, [okRows, groupId, isMock, dispatch, onDone])

  const okColumns: ColumnProps<OkRow>[] = [
    { title: '序号', dataIndex: 'seq', width: 60 },
    { title: '点位名称', dataIndex: 'name' },
    { title: '地址', dataIndex: 'address' },
    {
      title: '纬度',
      width: 100,
      render: (_t: unknown, r: OkRow) => r.lat.toFixed(4)
    },
    {
      title: '经度',
      width: 100,
      render: (_t: unknown, r: OkRow) => r.lng.toFixed(4)
    }
  ]

  const badColumns: ColumnProps<BadRow>[] = [
    { title: '序号', dataIndex: 'seq', width: 60 },
    { title: '点位名称', dataIndex: 'name' },
    {
      title: '失败原因',
      dataIndex: 'reason',
      render: (reason: string) => (
        <span className="poi-importer__reason">{reason}</span>
      )
    },
    { title: '纬度', dataIndex: 'latText', width: 100 },
    { title: '经度', dataIndex: 'lngText', width: 100 }
  ]

  if (step === 'upload') {
    return (
      <div className="poi-importer">
        <div className="poi-importer__steps">
          <div className="poi-importer__col">
            <div className="poi-importer__col-title">
              1. 下载模板，填写点位信息
            </div>
            <div className="poi-importer__tpl">
              <FileExcelOutlined className="poi-importer__tpl-icon" />
              <div className="poi-importer__tpl-name">《点位导入模板》</div>
              <div className="poi-importer__tpl-desc">
                包含：名称、地址、经纬度
              </div>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={downloadTemplate}
              >
                立即下载
              </Button>
            </div>
          </div>

          <div className="poi-importer__col">
            <div className="poi-importer__col-title">2. 上传填写完毕的文件</div>
            <Dragger
              accept=".xls,.xlsx"
              showUploadList={false}
              beforeUpload={handleFile}
              disabled={parsing}
            >
              <p className="poi-importer__drag-icon">
                {parsing ? <LoadingOutlined /> : <InboxOutlined />}
              </p>
              <p className="poi-importer__drag-title">
                {parsing ? '识别中…' : '点击选择文件'}
              </p>
              <p className="poi-importer__drag-hint">或将文件拖拽至此处上传</p>
              <p className="poi-importer__drag-sub">
                仅支持 .xls、.xlsx 格式文件
              </p>
              <p className="poi-importer__drag-sub">上传后将自动识别点位信息</p>
            </Dragger>
          </div>
        </div>

        <div className="poi-importer__footer">
          <Button onClick={onCancel}>取消</Button>
          <Button type="primary" disabled>
            确定
          </Button>
        </div>
      </div>
    )
  }

  // step === 'result'
  return (
    <div className="poi-importer">
      <div className="poi-importer__summary">
        共识别 <b>{okRows.length + badRows.length}</b> 个点位
        <span className="poi-importer__summary-ok">● 成功 {okRows.length}</span>
        {badRows.length > 0 && (
          <span className="poi-importer__summary-bad">
            ● 失败 {badRows.length}
          </span>
        )}
      </div>

      {okRows.length > 0 && (
        <>
          <div className="poi-importer__sub-title poi-importer__sub-title--ok">
            成功识别 {okRows.length} 个
          </div>
          <Table
            rowKey="seq"
            size="small"
            columns={okColumns}
            dataSource={okRows}
            pagination={false}
            scroll={{ y: 200 }}
          />
        </>
      )}

      {badRows.length > 0 && (
        <>
          <div className="poi-importer__sub-title poi-importer__sub-title--bad">
            识别失败 {badRows.length} 个
            <span className="poi-importer__sub-tip">
              将跳过，可后续手动添加
            </span>
          </div>
          <Table
            rowKey="seq"
            size="small"
            columns={badColumns}
            dataSource={badRows}
            pagination={false}
            scroll={{ y: 160 }}
          />
        </>
      )}

      <div className="poi-importer__footer">
        <Button onClick={onCancel}>取消</Button>
        <Button
          type="primary"
          loading={importing}
          disabled={okRows.length === 0}
          onClick={handleConfirm}
        >
          确定导入{okRows.length}个
        </Button>
      </div>
    </div>
  )
}

export default PoiImporter
